import React from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { StyledSimpleCard } from 'styles';
import { isTerminalMigrationStatus } from 'RealmModule/hooks/useRealmMigrations';
import { RealmMigrationSummary } from 'RealmModule/services/realmMigrationApi';

const STATUS_COLOR = (status: string) =>
  status === 'Failed' ? 'error' : status === 'Success' ? 'success' : 'info';

/**
 * Recent migrations for one tenant.
 *
 * Presentational: the page owns the query so that one poll feeds both this table and the
 * steps card's running/not-running decision. Status and duration come from galaxy's read
 * endpoint, which derives them from the child steps rather than the parent action-log row
 * — the parent closes within milliseconds because the data phase is fire-and-forget, so
 * it reports Success on a run that has hours left.
 */
export const MigrationHistoryCard = ({
  migrations,
  isLoading,
  isError,
  error,
  tenant,
  selectedId,
  onSelect,
}: {
  migrations: RealmMigrationSummary[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  /** Named in the empty state so it is obvious which tenant produced "none". */
  tenant?: string;
  selectedId: number | null;
  onSelect: (migrationLogId: number | null) => void;
}) => (
  <StyledSimpleCard>
    <h3>Recent Migrations{tenant ? ` — ${tenant}` : ''}</h3>

    {isLoading && (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress size={24} />
      </Box>
    )}

    {isError && (
      <Alert severity="error">
        Could not load migrations: {(error as any)?.data?.message ?? 'Unknown error'}
      </Alert>
    )}

    {!isLoading && !isError && migrations.length === 0 && (
      <Typography variant="body2" color="text.secondary">
        No migrations for {tenant ? `${tenant} ` : 'this tenant '}yet.
      </Typography>
    )}

    {migrations.length > 0 && (
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Team</TableCell>
              <TableCell>Source → Target</TableCell>
              <TableCell>Started</TableCell>
              <TableCell align="right">Duration</TableCell>
              <TableCell align="right">Steps</TableCell>
              <TableCell>Started by</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {migrations.map((m) => {
              // Steps are addressed by the parent action-log id. A row without one — a
              // recovery record whose originating log never resolved — has no step tree
              // to open, so it is inert rather than a click that silently does nothing.
              // It must also never compare equal to the initial `selectedId` of null,
              // which would render every such row as the current selection on load.
              const selectable = m.migrationLogId !== null;
              const select = selectable ? () => onSelect(m.migrationLogId) : undefined;
              return (
                <TableRow
                  key={`${m.migrationLogId ?? 'task'}-${m.taskId ?? ''}`}
                  hover={selectable}
                  tabIndex={selectable ? 0 : undefined}
                  role={selectable ? 'button' : undefined}
                  aria-label={
                    selectable ? `View steps for migration ${m.migrationLogId}` : undefined
                  }
                  selected={selectable && m.migrationLogId === selectedId}
                  onClick={select}
                  onKeyDown={
                    selectable
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            select?.();
                          }
                        }
                      : undefined
                  }
                  sx={{
                    cursor: selectable ? 'pointer' : 'default',
                    '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
                  }}
                >
                  <TableCell>
                    <Chip size="small" label={m.status} color={STATUS_COLOR(m.status)} />
                    {!m.tracked && (
                      <Tooltip title="Started through the data-only endpoint, which creates no recovery record — its status is best-effort and it cannot be resumed.">
                        <Chip size="small" label="untracked" sx={{ ml: 1 }} />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>{m.teamIds ?? '—'}</TableCell>
                  <TableCell>
                    {m.sourceRealmId ?? '—'} → {m.targetRealmId ?? '—'}
                  </TableCell>
                  <TableCell>
                    {m.startedAt ? new Date(m.startedAt).toLocaleString() : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {m.durationMinutes !== null ? `${m.durationMinutes}m` : '—'}
                    {!isTerminalMigrationStatus(m.status) && ' …'}
                  </TableCell>
                  <TableCell align="right">
                    {m.failedSteps > 0 ? `${m.failedSteps} failed / ` : ''}
                    {m.totalSteps}
                  </TableCell>
                  <TableCell>{m.initiatedBy ?? '—'}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    )}
  </StyledSimpleCard>
);

export default MigrationHistoryCard;
